import React, { useState, useRef, useEffect } from "react";
import { useTheme as useCustomTheme } from "../theme/useTheme";
import { useScopedDisabled } from "../hooks/useClientReadOnly";

export const CustomTextArea = ({
  placeholder,
  value,
  onChange,
  className = "",
  sx = {},
  required = false,
  disabled: disabledProp,
  rows = 4,
  maxRows,
  autoExpand = false,
  setValueOnClickAway = false,
  onInput,
  ...props
}) => {
  const disabled = useScopedDisabled(disabledProp);
  const { theme } = useCustomTheme();
  const [focused, setFocused] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [internalValue, setInternalValue] = useState(value ?? "");
  const textareaRef = useRef(null);

  useEffect(() => {
    if (setValueOnClickAway) {
      setInternalValue(value ?? "");
    }
  }, [value, setValueOnClickAway]);

  const palette = theme?.palette ?? {};
  const borderPalette = palette.border ?? {};
  const textPalette = palette.text ?? {};
  const defaultBorder = borderPalette.input ?? palette.divider ?? "#d9d9d9";
  const hoverBorder = borderPalette.default ?? palette.divider ?? "#bdbdbd";
  const focusBorder = "#000000"; // foco neutro en vez del color de marca
  const backgroundDefault = palette.background?.default ?? "#ffffff";
  const disabledBackground =
    palette.action?.disabledBackground ?? backgroundDefault;
  const fontFamily = theme?.typography?.fontFamily ?? "inherit";

  // Auto-expandir el textarea cuando cambia el valor
  const currentValue = setValueOnClickAway ? internalValue : value;

  useEffect(() => {
    if (autoExpand && textareaRef.current && maxRows) {
      const textarea = textareaRef.current;
      // Reset height to auto para calcular el scrollHeight correcto
      textarea.style.height = "auto";

      // Calcular la altura de una línea
      const lineHeight = parseInt(window.getComputedStyle(textarea).lineHeight);
      const paddingTop = parseInt(window.getComputedStyle(textarea).paddingTop);
      const paddingBottom = parseInt(window.getComputedStyle(textarea).paddingBottom);

      // Altura máxima basada en maxRows
      const maxHeight = lineHeight * maxRows + paddingTop + paddingBottom;

      // Calcular nueva altura
      const newHeight = Math.min(textarea.scrollHeight, maxHeight);

      // Aplicar nueva altura
      textarea.style.height = `${newHeight}px`;
    }
  }, [currentValue, autoExpand, maxRows]);

  // Manejo de clases CSS personalizadas para compatibilidad
  const parseCustomClasses = (className) => {
    const customStyles = {};

    if (className.includes("mt-1")) {
      customStyles.marginTop = "4px";
    }
    if (className.includes("min-h-[100px]")) {
      customStyles.minHeight = "100px";
    }

    return customStyles;
  };

  const customStyles = parseCustomClasses(className);

  const borderColor = disabled
    ? defaultBorder
    : focused
      ? focusBorder
      : hovered
        ? hoverBorder
        : defaultBorder;

  const boxShadow = !disabled && focused ? "none" : "none";

  const textColor = disabled
    ? textPalette.disabled ?? "#9e9e9e"
    : textPalette.primary ?? "#1f1f1f";

  const baseStyles = {
    width: "100%",
    minHeight: sx.minHeight || (autoExpand ? "35px" : "80px"),
    maxHeight: sx.maxHeight || "none",
    padding: "8px 12px",
    fontSize: "14px",
    lineHeight: 1.4,
    fontFamily,
    backgroundColor: disabled ? disabledBackground : backgroundDefault,
    border: `1px solid ${borderColor}`,
    borderRadius: "8px",
    outline: "none",
    transition: "all 0.2s ease",
    color: textColor,
    resize: sx.resize || (autoExpand ? "none" : "vertical"),
    overflow: sx.overflow || "auto",
    boxShadow,
    ...customStyles,
    ...sx,
  };

  return (
    <textarea
      ref={textareaRef}
      placeholder={placeholder}
      value={currentValue}
      onChange={(e) => {
        if (setValueOnClickAway) {
          setInternalValue(e.target.value);
        } else {
          onChange(e);
        }

        if (onInput) {
          onInput(e);
        }
      }}
      required={required}
      disabled={disabled}
      rows={rows}
      onFocus={() => {
        if (!disabled) {
          setFocused(true);
          setHovered(false);
        }
      }}
      onBlur={(e) => {
        setFocused(false);
        setHovered(false);

        if (setValueOnClickAway && internalValue !== (value ?? "")) {
          const syntheticEvent = {
            ...e,
            target: {
              ...e.target,
              value: internalValue,
            },
          };
          onChange(syntheticEvent);
        }

        if (props.onBlur) {
          props.onBlur(e);
        }
      }}
      onMouseEnter={() => {
        if (!focused && !disabled) {
          setHovered(true);
        }
      }}
      onMouseLeave={() => {
        setHovered(false);
      }}
      style={baseStyles}
      {...props}
    />
  );
};
